<template>
    <div class="modal image-upload-modal">
        <div class="image-upload">
            <base-modal-close @close-modal="$emit('close-modal')" />
            <div class="image-input-container">
                <div v-if="loadingImage">
                    <i class="fas fa-spinner fa-spin loader" />
                </div>
                <input
                    v-if="!file"
                    id="files"
                    ref="avatar"
                    type="file"
                    accept="image/*"
                    @change="handleAvatar"
                >
                <div
                    v-if="!file"
                    class="image-input-cover flex"
                >
                    <template v-if="!fileLoaded">
                        Click or drag image to upload.
                        <span
                            class="upload-info"
                        >
                            Maximum file size 2mb, allowed types: jpg, jpeg, png
                        </span>
                    </template>
                </div>
                <div
                    v-else
                    class="image-crop"
                >
                    <vue-cropper
                        ref="cropper"
                        class="crop-window"
                        :class="{'crop-window-loading': loadingImage}"
                        :guides="true"
                        :aspect-ratio="1 / 1"
                        :min-container-width="250"
                        :min-container-height="180"
                        :min-crop-box-width="100"
                        :min-crop-box-height="100"
                        :background="false"
                        :view-mode="2"
                        :img-style="{ 'width': '100%', 'height': 'auto' }"
                        :src="croppedFile"
                        @ready="imageLoaded"
                    />
                </div>
                <base-submit-button
                    v-show="file"
                    class="btn"
                    :loading="uploading"
                    @click="submitAvatar"
                >
                    Set photo
                </base-submit-button>
            </div>
        </div>
    </div>
</template>

<script>
import VueCropper from 'vue-cropperjs';
import { mapActions } from 'vuex';

import toast from '@/mixins/toasts';
import modal from '@/mixins/modal';

export default {
    components: {
        VueCropper,
    },

    mixins: [
        toast,
        modal,
    ],

    data: () => ({
        file: null,
        croppedFile: null,
        uploading: false,
        fileSizeLimit: 2097152,
        fileLoaded: false,
        loadingImage: false,
    }),

    /**
     * Before destroy hook
     */
    beforeDestroy() {
        if (this.$refs.cropper) {
            this.clearCanvas();
        }
    },

    methods: {
        ...mapActions(['setNewAvatar']),

        /**
         * Handles input change
         *
         */
        handleAvatar() {
            this.file = this.$refs.avatar.files[0];

            const sizeValidation = this.validateSize();

            if (! sizeValidation) {
                this.file = null;

                this.toastError({
                    message: {
                        title: 'Avatar image can be maximum 2mb',
                    },
                });
            } else {
                this.loadingImage = true;
                const reader = new FileReader();
                reader.onload = (event) => {
                    this.imageSrc = event.target.result;

                    this.$refs.cropper.replace(event.target.result);
                };
                reader.readAsDataURL(this.file);
                this.fileLoaded = true;
            }
        },

        /**
         * Validates file size
         *
         *  @returns {boolean} If size matches.
         */
        validateSize() {
            return (this.fileSizeLimit > this.file.size);
        },

        /**
         * Sets loading image flag
         *
         */
        imageLoaded() {
            this.loadingImage = false;
        },

        /**
         * Crops image
         */
        cropImage() {
            this.croppedFile = this.$refs.cropper.getCroppedCanvas().toDataURL();
        },

        /**
         * Clears cropper canvas
         *
         */
        clearCanvas() {
            this.file = null;
            this.fileLoaded = false;
            this.$refs.cropper.destroy();
        },

        /**
         * Submits avatar image
         *
         */
        submitAvatar() {
            this.$refs.cropper.getCroppedCanvas().toBlob((blob) => {
                const formData = new FormData();
                formData.append('avatar', blob);

                this.uploading = true;

                this.$http.post(`${this.$appUrl}/api/user/avatar`,
                    formData,
                    {
                        headers: {
                            'Content-Type': 'multipart/form-data',
                        },
                    }
                )
                    .then(res => {
                        this.setNewAvatar(res.data);
                        this.uploading = false;
                        this.toastSuccess({
                            message: {
                                title: 'Your avatar image has been changed!',
                            },
                        });
                        this.$emit('close-modal');
                    }
                    )
                    .catch(() => {
                        this.uploading = false;
                        this.toastError({
                            message: {
                                title: 'Ups! Something went wrong. Please try again later.',
                            },
                        });
                    });
            }, 'image/jpeg', 0.70);
        },
    },
};
</script>
